/**
 * Main Application Component
 * Handles routing and global providers
 */
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import LoadingSpinner from './components/common/LoadingSpinner';
import './styles/App.css';

// Lazy load pages for better performance
const HomePage = lazy(() => import('./pages/HomePage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const Payment = lazy(() => import('./pages/Payment'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const EvaluatorPage = lazy(() => import('./pages/EvaluatorPage'));
const JuryEvaluationPage = lazy(() => import('./pages/JuryEvaluationPage'));
const RankingsPage = lazy(() => import('./pages/RankingsPage'));
const PartnerPage = lazy(() => import('./pages/PartnerPage'));
const MentorPage = lazy(() => import('./pages/MentorPage'));
const ShortlistedTeamsPage = lazy(() => import('./pages/ShortlistedTeamsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

/**
 * Loading fallback component for suspense
 */
const PageLoader = () => (
  <div className="page-loader">
    <LoadingSpinner size="large" />
    <p>Loading...</p>
  </div>
);

/**
 * Main App component with routing configuration
 */
function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="app">
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/partner" element={<PartnerPage />} />
              <Route path="/mentor" element={<MentorPage />} />
              <Route path="/shortlisted-teams" element={<ShortlistedTeamsPage />} />
              
              {/* Protected Routes - Require Authentication */}
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/payment" 
                element={
                  <ProtectedRoute>
                    <Payment />
                  </ProtectedRoute>
                } 
              />
              
              {/* Admin Routes */}
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/evaluator" element={<EvaluatorPage />} />
              
              {/* Jury Evaluation Route */}
              <Route path="/jury-evaluation" element={<JuryEvaluationPage />} />
              
              {/* Rankings Route */}
              <Route path="/rankings" element={<RankingsPage />} />
              
              {/* 404 Page */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
